import { dec } from "./money.js";

/**
 * Line-level classification (plan §14.3; DES-61, DES-98, DES-96). Pure: the
 * Worker applies it at ingest and retroactively, the web app uses it for the
 * review queue and the RTS base.
 *
 * Precedence for a line, highest first:
 *   1. the user's own decision on the line (classification_source USER) — never overridden;
 *   2. SUPPLIER_CABYS: the taxpayer's rule for this supplier + CABYS code (expense lines);
 *   3. SUPPLIER:       the taxpayer's rule for this supplier (expense lines);
 *   4. CUSTOMER:       the taxpayer's rule for this customer (income lines);
 *   5. CABYS:          the taxpayer's rule for this CABYS code (either direction);
 *   6. TEXT:           the line description contains the rule's text, ignoring case and
 *                      accents (either direction); the longest text wins, then alphabetical;
 *   7. unclassified:   the XML facts only (DEFAULT) -> an expense needs review.
 *
 * A rule competes for a line only when it has something to say for the line's
 * direction: EXPENSE_KEYS for expenses, INCOME_KEYS for sales. A rule that only
 * sets the business use (TRANSACTION_KEYS) competes in both. The winning rule
 * decides the whole line (one source per line).
 *
 * Rules never change facts from the XML:
 *   - an expense line that carries no IVA can never become creditable;
 *   - a sale is TAXED only when the XML charges IVA, and a rule never makes a taxed sale
 *     untaxed; an exonerated sale or an export (FEE) stays what the XML says. A rule only
 *     chooses between EXEMPT, EXPORT and NON_SUBJECT for the other sales;
 *   - a rule's IVA rate only fills a taxed sale whose XML gives no rate.
 *
 * Two more decisions carry their own "the user decided" mark, which rules
 * never override:
 *   - the transaction's business use (business_use_source USER). Otherwise it
 *     comes from the highest-ranked winning rule of its lines that sets one
 *     (ties: the first line); for a transaction without lines (manual, CSV),
 *     from the rule that wins for its counterparty or its description. When no
 *     rule sets it any more, a rule's choice falls back to BUSINESS;
 *   - the RTS category of an expense line (rts_category_source USER), DES-96.
 *     Otherwise the winning rule's, if it sets one. Without either,
 *     effectiveRtsCategory() derives it from the accounting category or the
 *     CABYS code, marked as derived.
 */

export type IvaCreditType = "FULL" | "PRORRATA" | "NONE";
export type RuleMatchType = "SUPPLIER_CABYS" | "SUPPLIER" | "CUSTOMER" | "CABYS" | "TEXT";
export type LineClassificationSource = "USER" | "SUPPLIER_CABYS_RULE" | "SUPPLIER_RULE" | "CUSTOMER_RULE" | "CABYS_RULE" | "TEXT_RULE" | "DEFAULT";
export type ReviewStatus = "NEEDS_REVIEW" | "CLASSIFIED" | "CONFIRMED";
export type FlowDirection = "INCOME" | "EXPENSE";
export type BusinessUse = "BUSINESS" | "PERSONAL" | "MIXED";
/** IVA treatment of a sale (transaction_lines.tax_category on income lines). */
export type SaleTaxCategory = "TAXED" | "EXEMPT" | "EXPORT" | "NON_SUBJECT";
/** RTS base component of an expense (Decreto 43881-H art. 4), DES-96. */
export type RtsCategory = "PURCHASE" | "SERVICES_SALARIES" | "PRODUCTION_COST" | "FUEL" | "OPENING_INVENTORY" | "EXCLUDED";

/** What a rule (or a user decision) sets. Absent or null keys mean "not set". */
export interface RuleSets {
  // expense lines
  accounting_category?: string | null;
  income_tax_deductible?: boolean | null;
  iva_credit_type?: IvaCreditType | null;
  rts_category?: RtsCategory | null;
  // income lines
  tax_category?: SaleTaxCategory | null;
  /** Fraction as a decimal string ("0.13"); fills a taxed sale without rate. */
  iva_rate?: string | null;
  // the transaction
  business_use?: BusinessUse | null;
  /** Business share of a MIXED use, "0".."100" with up to two decimals (a string: ADR-0003). */
  business_pct?: string | null;
}

export const EXPENSE_KEYS = ["accounting_category", "income_tax_deductible", "iva_credit_type", "rts_category"] as const satisfies readonly (keyof RuleSets)[];
export const INCOME_KEYS = ["tax_category", "iva_rate"] as const satisfies readonly (keyof RuleSets)[];
export const TRANSACTION_KEYS = ["business_use", "business_pct"] as const satisfies readonly (keyof RuleSets)[];

export interface ClassificationRule {
  id: string;
  match_type: string;
  match_value: string;
  sets: RuleSets | Record<string, unknown>;
}

export interface LineClassification {
  accounting_category: string | null;
  income_tax_deductible: boolean | null;
  iva_creditable: boolean;
  iva_credit_type: IvaCreditType;
  classification_source: LineClassificationSource;
  classification_rule_id: string | null;
  /** numeric(4,3) as a decimal string; null when nothing classified the line. */
  confidence: string | null;
}

/** Chart of expense categories offered in the review queue (accounting_category). */
export const ACCOUNTING_CATEGORIES: readonly { code: string; label: string }[] = [
  { code: "COST_OF_SALES", label: "Inventario / costo de ventas" },
  { code: "RENT", label: "Alquileres" },
  { code: "UTILITIES", label: "Servicios públicos (luz, agua, teléfono, internet)" },
  { code: "PROFESSIONAL_SERVICES", label: "Servicios profesionales" },
  { code: "SOFTWARE", label: "Software y suscripciones" },
  { code: "OFFICE_SUPPLIES", label: "Suministros de oficina" },
  { code: "ADVERTISING", label: "Publicidad y mercadeo" },
  { code: "FUEL", label: "Combustible" },
  { code: "VEHICLE", label: "Vehículos y mantenimiento" },
  { code: "TRAVEL", label: "Viáticos y transporte" },
  { code: "MEALS", label: "Alimentación y representación" },
  { code: "BANK_FEES", label: "Comisiones bancarias" },
  { code: "INSURANCE", label: "Seguros" },
  { code: "TAXES_AND_FEES", label: "Impuestos, patentes y timbres" },
  { code: "FIXED_ASSET", label: "Activo fijo (se deprecia)" },
  { code: "TRAINING", label: "Capacitación" },
  { code: "OTHER", label: "Otros gastos" },
  { code: "PERSONAL", label: "Personal (no es del negocio)" },
];

export const SALE_TAX_CATEGORIES: readonly { code: SaleTaxCategory; label: string }[] = [
  { code: "TAXED", label: "Gravada (con IVA)" },
  { code: "EXEMPT", label: "Exenta" },
  { code: "EXPORT", label: "Exportación" },
  { code: "NON_SUBJECT", label: "No sujeta" },
];

export const BUSINESS_USES: readonly { code: BusinessUse; label: string }[] = [
  { code: "BUSINESS", label: "Negocio" },
  { code: "PERSONAL", label: "Personal" },
  { code: "MIXED", label: "Mixto" },
];

/** Decreto 43881-H art. 4 (as amended by 45209-H); docs/tax/RTS.md §4. */
export const RTS_CATEGORIES: readonly { code: RtsCategory; label: string; short: string }[] = [
  { code: "PURCHASE", label: "Compras de mercancías, materias primas e insumos", short: "Compras" },
  { code: "SERVICES_SALARIES", label: "Servicios personales o profesionales y salarios", short: "Servicios y salarios" },
  { code: "PRODUCTION_COST", label: "Mano de obra y otros costos de producción (electricidad, teléfono, agua, alquileres)", short: "Costos de producción" },
  { code: "FUEL", label: "Combustible", short: "Combustible" },
  { code: "OPENING_INVENTORY", label: "Inventario al ingresar al régimen (art. 9)", short: "Inventario inicial" },
  { code: "EXCLUDED", label: "No forma parte de la base RTS", short: "Fuera de la base" },
];

/** Legal IVA rates as fractions (Ley 6826 arts. 10-11). */
export const IVA_RATES = ["0.13", "0.04", "0.02", "0.01", "0.005"] as const;

const CATEGORY_CODES = new Set(ACCOUNTING_CATEGORIES.map((c) => c.code));
const CREDIT_TYPES = new Set<string>(["FULL", "PRORRATA", "NONE"]);
const SALE_CODES = new Set<string>(SALE_TAX_CATEGORIES.map((c) => c.code));
const USE_CODES = new Set<string>(BUSINESS_USES.map((c) => c.code));
const RTS_CODES = new Set<string>(RTS_CATEGORIES.map((c) => c.code));
const RATE_CODES = new Set<string>(IVA_RATES);
const MATCH_TYPES = new Set<string>(["SUPPLIER_CABYS", "SUPPLIER", "CUSTOMER", "CABYS", "TEXT"]);
const ALLOWED_KEYS = new Set<string>([...EXPENSE_KEYS, ...INCOME_KEYS, ...TRANSACTION_KEYS]);
/** A Costa Rican ID without leading zeros: 9 (física) to 12 (DIMEX) digits. */
const PARTY_ID = /^[1-9]\d{8,11}$/;

/** Supplier / customer IDs are compared as digits without leading zeros (the ledger stores them unpadded). */
export function normalizeSupplierId(id: string | null | undefined): string | null {
  const d = (id ?? "").replace(/\D/g, "").replace(/^0+/, "");
  return d === "" ? null : d;
}
export const normalizeCounterpartyId = normalizeSupplierId;

/** CABYS codes are 13 digits; anything else is not a CABYS code. */
export function normalizeCabys(code: string | null | undefined): string | null {
  const d = (code ?? "").replace(/\s/g, "");
  return /^\d{13}$/.test(d) ? d : null;
}

/** Text as TEXT rules compare it: accents and case removed, whitespace collapsed. */
export function normalizeText(s: string | null | undefined): string {
  return (s ?? "").normalize("NFD").replace(/\p{M}+/gu, "").toLowerCase().replace(/\s+/g, " ").trim();
}

export const TEXT_RULE_MIN = 3;
export const TEXT_RULE_MAX = 100;

/** The stored form of a TEXT rule, or null when too short or too long. */
export function textRuleValue(text: string | null | undefined): string | null {
  const n = normalizeText(text);
  const len = [...n].length;
  return len >= TEXT_RULE_MIN && len <= TEXT_RULE_MAX ? n : null;
}

/** Whether a TEXT rule's text appears in a description (case and accents ignored). */
export function textMatches(description: string | null | undefined, text: string | null | undefined): boolean {
  const p = textRuleValue(text);
  return p !== null && normalizeText(description).includes(p);
}

/**
 * The match value a rule of `type` has for these facts (null = no rule of
 * that type can match). `party` is the supplier for SUPPLIER / SUPPLIER_CABYS
 * and the customer for CUSTOMER.
 */
export function ruleMatchValue(type: RuleMatchType, party: string | null, cabys: string | null, text: string | null = null): string | null {
  const s = normalizeSupplierId(party);
  const c = normalizeCabys(cabys);
  switch (type) {
    case "SUPPLIER":
    case "CUSTOMER":
      return s;
    case "CABYS":
      return c;
    case "SUPPLIER_CABYS":
      return s && c ? `${s}|${c}` : null;
    case "TEXT":
      return textRuleValue(text);
  }
}

export type RuleValidation = { ok: true; rule: { match_type: RuleMatchType; match_value: string; sets: RuleSets } } | { ok: false; reason: string };

const MISSING_VALUE: Record<RuleMatchType, string> = {
  CABYS: "Código CABYS inválido (13 dígitos)",
  SUPPLIER: "Identificación del proveedor inválida",
  CUSTOMER: "Identificación del cliente inválida",
  SUPPLIER_CABYS: "Faltan el proveedor o el CABYS",
  TEXT: `El texto debe tener entre ${TEXT_RULE_MIN} y ${TEXT_RULE_MAX} caracteres`,
};

/** Validate and normalize a rule before it is stored (the DB re-checks the shape: private.classification_sets_valid). */
export function validateRule(input: { match_type: unknown; supplier_id?: unknown; customer_id?: unknown; cabys?: unknown; text?: unknown; sets: unknown }): RuleValidation {
  const type = input.match_type;
  if (typeof type !== "string" || !MATCH_TYPES.has(type)) return { ok: false, reason: "Tipo de regla no válido" };
  const t = type as RuleMatchType;
  const str = (v: unknown) => (typeof v === "string" ? v : null);
  const party = t === "CUSTOMER" ? str(input.customer_id) : str(input.supplier_id);
  const value = ruleMatchValue(t, party, str(input.cabys), str(input.text));
  if (!value) return { ok: false, reason: MISSING_VALUE[t] };
  if ((t === "SUPPLIER" || t === "CUSTOMER" || t === "SUPPLIER_CABYS") && !PARTY_ID.test(value.split("|")[0]!)) {
    return { ok: false, reason: t === "CUSTOMER" ? MISSING_VALUE.CUSTOMER : MISSING_VALUE.SUPPLIER };
  }
  const sets = validateSets(input.sets, t);
  if (!sets.ok) return sets;
  return { ok: true, rule: { match_type: t, match_value: value, sets: sets.sets } };
}

const present = (v: unknown) => v !== undefined && v !== null;
const hasAny = (sets: RuleSets, keys: readonly (keyof RuleSets)[]) => keys.some((k) => present(sets[k]));

/**
 * Validate what a rule sets. `matchType` adds the per-type limits (supplier
 * rules classify expenses, customer rules sales). Keep in step with
 * private.classification_sets_valid (supabase/tests/rules.test.ts runs both).
 */
export function validateSets(raw: unknown, matchType?: string | null): { ok: true; sets: RuleSets } | { ok: false; reason: string } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, reason: "Clasificación vacía" };
  const r = raw as Record<string, unknown>;
  const unknownKey = Object.keys(r).find((k) => !ALLOWED_KEYS.has(k));
  if (unknownKey) return { ok: false, reason: `Campo no permitido: ${unknownKey}` };
  const sets: RuleSets = {};
  if (present(r.accounting_category)) {
    if (typeof r.accounting_category !== "string" || !CATEGORY_CODES.has(r.accounting_category)) return { ok: false, reason: "Categoría contable desconocida" };
    sets.accounting_category = r.accounting_category;
  }
  if (present(r.income_tax_deductible)) {
    if (typeof r.income_tax_deductible !== "boolean") return { ok: false, reason: "Deducibilidad inválida" };
    sets.income_tax_deductible = r.income_tax_deductible;
  }
  if (present(r.iva_credit_type)) {
    if (typeof r.iva_credit_type !== "string" || !CREDIT_TYPES.has(r.iva_credit_type)) return { ok: false, reason: "Tipo de crédito de IVA inválido" };
    sets.iva_credit_type = r.iva_credit_type as IvaCreditType;
  }
  if (present(r.rts_category)) {
    if (typeof r.rts_category !== "string" || !RTS_CODES.has(r.rts_category)) return { ok: false, reason: "Categoría RTS desconocida" };
    sets.rts_category = r.rts_category as RtsCategory;
  }
  if (present(r.tax_category)) {
    if (typeof r.tax_category !== "string" || !SALE_CODES.has(r.tax_category)) return { ok: false, reason: "Tratamiento de IVA de la venta desconocido" };
    sets.tax_category = r.tax_category as SaleTaxCategory;
  }
  if (present(r.iva_rate)) {
    if (typeof r.iva_rate !== "string" || !RATE_CODES.has(r.iva_rate)) return { ok: false, reason: "Tarifa de IVA inválida (13 %, 4 %, 2 %, 1 % o 0,5 %)" };
    sets.iva_rate = r.iva_rate;
  }
  if (present(r.business_use)) {
    if (typeof r.business_use !== "string" || !USE_CODES.has(r.business_use)) return { ok: false, reason: "Uso inválido (negocio, personal o mixto)" };
    sets.business_use = r.business_use as BusinessUse;
  }
  if (present(r.business_pct)) {
    const p = r.business_pct;
    if (typeof p !== "string" || !/^\d{1,3}(\.\d{1,2})?$/.test(p) || dec(p).gt(100)) return { ok: false, reason: "Porcentaje de uso del negocio inválido (0 a 100, hasta dos decimales)" };
    sets.business_pct = p;
  }
  if (Object.keys(sets).length === 0) return { ok: false, reason: "Indique al menos un dato de clasificación" };
  if ((sets.business_use === "MIXED") !== present(sets.business_pct)) {
    return { ok: false, reason: sets.business_use === "MIXED" ? "Indique el porcentaje de uso del negocio" : "El porcentaje solo aplica al uso mixto" };
  }
  if (present(sets.iva_rate) && present(sets.tax_category) && sets.tax_category !== "TAXED") return { ok: false, reason: "La tarifa de IVA solo aplica a ventas gravadas" };
  const expense = hasAny(sets, EXPENSE_KEYS);
  const income = hasAny(sets, INCOME_KEYS);
  if (expense && income) return { ok: false, reason: "Una regla clasifica gastos o ventas, no ambos" };
  if ((matchType === "SUPPLIER" || matchType === "SUPPLIER_CABYS") && income) return { ok: false, reason: "Las reglas por proveedor clasifican gastos" };
  if (matchType === "CUSTOMER" && expense) return { ok: false, reason: "Las reglas por cliente clasifican ventas" };
  return { ok: true, sets };
}

/** A line carries IVA that could be credited (fact from the XML, never from a rule). */
export function lineIsTaxed(line: { tax_category: string | null }): boolean {
  return line.tax_category === "TAXED";
}

/** Apply `sets` to an EXPENSE line, enforcing the XML facts. */
export function applySets(line: { tax_category: string | null }, sets: RuleSets, source: LineClassificationSource, ruleId: string | null): LineClassification {
  const taxed = lineIsTaxed(line);
  const credit: IvaCreditType = taxed ? (sets.iva_credit_type ?? "FULL") : "NONE";
  return {
    accounting_category: sets.accounting_category ?? null,
    income_tax_deductible: sets.income_tax_deductible ?? null,
    iva_creditable: credit !== "NONE",
    iva_credit_type: credit,
    classification_source: source,
    classification_rule_id: ruleId,
    confidence: source === "DEFAULT" ? null : "1",
  };
}

/** Unclassified: only what the XML says (the ingest defaults). */
export function defaultClassification(line: { tax_category: string | null }): LineClassification {
  return applySets(line, {}, "DEFAULT", null);
}

// ------------------------------------------------------------------ sales

export interface IncomeLineClassification {
  tax_category: string | null;
  iva_rate: string | null;
  classification_source: LineClassificationSource;
  classification_rule_id: string | null;
  confidence: string | null;
}

/** What the XML says about a sale line. */
export interface SaleFacts {
  tax_category: string | null;
  iva_rate: string | null;
  /** The line carries an exoneration (exemption node). */
  exonerated: boolean;
}

/**
 * The XML facts of an income line, or null when they cannot be known (not
 * loaded; or a row older than migration 2200 that a rule already changed).
 */
export function saleFacts(line: {
  tax_category: string | null;
  iva_rate?: string | null;
  exemption?: unknown;
  xml_tax_category?: string | null;
  xml_iva_rate?: string | null;
  classification_source: string | null;
}): SaleFacts | null {
  if (line.xml_tax_category === undefined) return null;
  const exonerated = present(line.exemption);
  if (line.xml_tax_category === null && (line.xml_iva_rate ?? null) === null && (line.tax_category !== null || (line.iva_rate ?? null) !== null)) {
    // Facts never captured: safe to read from the line only while nothing reclassified it.
    return lineIsClassified(line) ? null : { tax_category: line.tax_category, iva_rate: line.iva_rate ?? null, exonerated };
  }
  return { tax_category: line.xml_tax_category, iva_rate: line.xml_iva_rate ?? null, exonerated };
}

/** Whether the XML settles a sale's IVA treatment (IVA charged, exonerated or an export). */
export function saleTreatmentSettled(f: SaleFacts): boolean {
  return f.exonerated || f.tax_category === "TAXED" || f.tax_category === "EXPORT";
}

/** Apply `sets` to an INCOME line, enforcing the XML facts. */
export function applyIncomeSets(facts: SaleFacts, sets: RuleSets, source: LineClassificationSource, ruleId: string | null): IncomeLineClassification {
  let category = facts.tax_category;
  let rate = facts.iva_rate;
  if (!saleTreatmentSettled(facts) && sets.tax_category && sets.tax_category !== "TAXED") category = sets.tax_category;
  if (category === "TAXED" && rate === null && sets.iva_rate) rate = sets.iva_rate;
  return { tax_category: category, iva_rate: rate, classification_source: source, classification_rule_id: ruleId, confidence: source === "DEFAULT" ? null : "1" };
}

// ------------------------------------------------------------------ rule lookup

const SOURCE_BY_TYPE: Record<RuleMatchType, LineClassificationSource> = {
  SUPPLIER_CABYS: "SUPPLIER_CABYS_RULE",
  SUPPLIER: "SUPPLIER_RULE",
  CUSTOMER: "CUSTOMER_RULE",
  CABYS: "CABYS_RULE",
  TEXT: "TEXT_RULE",
};

/** Rule types in precedence order (after the user's own decision). */
export const RULE_PRECEDENCE: readonly RuleMatchType[] = ["SUPPLIER_CABYS", "SUPPLIER", "CUSTOMER", "CABYS", "TEXT"];
const RANK = new Map(RULE_PRECEDENCE.map((t, i) => [t, i + 1]));

export class RuleIndex {
  private readonly byKey = new Map<string, ClassificationRule>();
  /** TEXT rules, most specific first: longest text, then alphabetical, then id. */
  private readonly texts: { text: string; rule: ClassificationRule }[] = [];
  constructor(rules: readonly ClassificationRule[]) {
    for (const r of rules) {
      if (r.match_type === "TEXT") {
        const text = textRuleValue(r.match_value);
        if (text) this.texts.push({ text, rule: r });
      } else {
        this.byKey.set(`${r.match_type}:${r.match_value}`, r);
      }
    }
    const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
    this.texts.sort((a, b) => [...b.text].length - [...a.text].length || cmp(a.text, b.text) || cmp(a.rule.id, b.rule.id));
  }
  get(type: RuleMatchType, value: string | null): ClassificationRule | undefined {
    return value === null ? undefined : this.byKey.get(`${type}:${value}`);
  }
  /** The rules that match, in precedence order. */
  *candidates(direction: FlowDirection, facts: MatchFacts): Generator<ClassificationRule> {
    const types: RuleMatchType[] = direction === "EXPENSE" ? ["SUPPLIER_CABYS", "SUPPLIER", "CABYS"] : ["CUSTOMER", "CABYS"];
    for (const t of types) {
      const r = this.get(t, ruleMatchValue(t, facts.counterparty, facts.cabys));
      if (r) yield r;
    }
    const d = normalizeText(facts.description);
    if (d) for (const { text, rule } of this.texts) if (d.includes(text)) yield rule;
  }
}

export interface MatchFacts {
  /** The counterparty's ID number: the supplier on expenses, the customer on sales. */
  counterparty: string | null;
  cabys: string | null;
  description: string | null;
}

export interface RuleMatch {
  rule: ClassificationRule;
  sets: RuleSets;
  source: LineClassificationSource;
  /** 1 = SUPPLIER_CABYS ... 5 = TEXT (RULE_PRECEDENCE). */
  rank: number;
}

/**
 * Whether a rule has something to say. For a line: keys of its direction, or
 * only the business use. For a transaction without lines: the business use,
 * or (expenses) the RTS category.
 */
function competes(sets: RuleSets, direction: FlowDirection, scope: "line" | "transaction"): boolean {
  const own = hasAny(sets, direction === "EXPENSE" ? EXPENSE_KEYS : INCOME_KEYS);
  const other = hasAny(sets, direction === "EXPENSE" ? INCOME_KEYS : EXPENSE_KEYS);
  const tx = hasAny(sets, TRANSACTION_KEYS);
  const forLine = own || (!other && tx);
  if (scope === "line") return forLine;
  return forLine && (tx || (direction === "EXPENSE" && present(sets.rts_category)));
}

/** The rule that decides a line (or a transaction without lines), ignoring any user decision. */
export function winningRule(direction: FlowDirection, facts: MatchFacts, index: RuleIndex, scope: "line" | "transaction" = "line"): RuleMatch | null {
  for (const rule of index.candidates(direction, facts)) {
    const v = validateSets(rule.sets, rule.match_type);
    // A malformed stored rule is skipped, never half-applied.
    if (!v.ok || !competes(v.sets, direction, scope)) continue;
    const type = rule.match_type as RuleMatchType;
    return { rule, sets: v.sets, source: SOURCE_BY_TYPE[type], rank: RANK.get(type) ?? RULE_PRECEDENCE.length + 1 };
  }
  return null;
}

/**
 * Classify one EXPENSE line. Returns null for a user decision (it stays as
 * it is); otherwise the classification the rules give, or the default.
 */
export function classifyExpenseLine(
  line: { cabys: string | null; tax_category: string | null; classification_source: string | null; description?: string | null },
  supplierId: string | null,
  rules: RuleIndex,
): LineClassification | null {
  if (line.classification_source === "USER") return null;
  const w = winningRule("EXPENSE", { counterparty: supplierId, cabys: line.cabys, description: line.description ?? null }, rules);
  return w ? applySets(line, w.sets, w.source, w.rule.id) : defaultClassification(line);
}

/**
 * Classify one INCOME line. Returns null for a user decision, or when the
 * line's XML facts are unknown (it stays as it is).
 */
export function classifyIncomeLine(
  line: Parameters<typeof saleFacts>[0] & { cabys: string | null; description?: string | null },
  customerId: string | null,
  rules: RuleIndex,
): IncomeLineClassification | null {
  if (line.classification_source === "USER") return null;
  const facts = saleFacts(line);
  if (!facts) return null;
  const w = winningRule("INCOME", { counterparty: customerId, cabys: line.cabys, description: line.description ?? null }, rules);
  return w ? applyIncomeSets(facts, w.sets, w.source, w.rule.id) : applyIncomeSets(facts, {}, "DEFAULT", null);
}

export interface RtsAssignment {
  rts_category: RtsCategory | null;
  rts_category_source: "RULE" | null;
}

/** The RTS category a rule gives (null = the user's own choice stays). */
export function rtsFromRule(current: { rts_category_source?: string | null }, winner: RuleMatch | null): RtsAssignment | null {
  if (current.rts_category_source === "USER") return null;
  const c = winner?.sets.rts_category ?? null;
  return { rts_category: c, rts_category_source: c ? "RULE" : null };
}

export interface BusinessUseAssignment {
  business_use: BusinessUse;
  business_pct: string | null;
  business_use_source: "RULE" | "DEFAULT";
}

/**
 * The business use the rules give a transaction, from the winning rules of
 * its lines (highest rank; ties: the first line). Null = keep it: a person's
 * choice, or the imported default when no rule says anything.
 */
export function businessUseFromRules(current: { business_use_source?: string | null }, winners: readonly (RuleMatch | null)[]): BusinessUseAssignment | null {
  if (current.business_use_source === "USER") return null;
  let best: RuleMatch | null = null;
  for (const w of winners) if (w?.sets.business_use && (!best || w.rank < best.rank)) best = w;
  if (best?.sets.business_use) {
    const use = best.sets.business_use;
    return { business_use: use, business_pct: use === "MIXED" ? (best.sets.business_pct ?? null) : null, business_use_source: "RULE" };
  }
  return current.business_use_source === "RULE" ? { business_use: "BUSINESS", business_pct: null, business_use_source: "DEFAULT" } : null;
}

export function lineIsClassified(line: { classification_source: string | null }): boolean {
  return line.classification_source !== null && line.classification_source !== "DEFAULT";
}

/**
 * Review status of a transaction from its lines.
 *  - `fresh` (ingest): EXPENSE with any unclassified line -> NEEDS_REVIEW, else CLASSIFIED.
 *  - `upgrade` (retroactive / after a user decision): only NEEDS_REVIEW -> CLASSIFIED;
 *    never re-flags something a person already marked.
 *  - A transaction without lines (manual, CSV) is reviewed for its business use:
 *    NEEDS_REVIEW -> CLASSIFIED once a person or a rule set it (`businessUseSource`).
 * CONFIRMED is final; INCOME transactions with lines keep their status.
 */
export function reviewStatusFor(
  current: ReviewStatus | string,
  direction: FlowDirection | string,
  lines: readonly { classification_source: string | null }[],
  mode: "fresh" | "upgrade",
  businessUseSource?: string | null,
): ReviewStatus | string {
  if (current === "CONFIRMED") return current;
  if (lines.length === 0) return current === "NEEDS_REVIEW" && (businessUseSource === "USER" || businessUseSource === "RULE") ? "CLASSIFIED" : current;
  if (direction !== "EXPENSE") return current;
  const all = lines.every(lineIsClassified);
  if (mode === "fresh") return all ? "CLASSIFIED" : "NEEDS_REVIEW";
  return all && current === "NEEDS_REVIEW" ? "CLASSIFIED" : current;
}

// ------------------------------------------------------------------ one transaction

export interface ClassifiableLine {
  cabys: string | null;
  description?: string | null;
  tax_category: string | null;
  iva_rate?: string | null;
  exemption?: unknown;
  xml_tax_category?: string | null;
  xml_iva_rate?: string | null;
  classification_source: string | null;
  rts_category_source?: string | null;
}

export interface ClassifiableTransaction {
  direction: string;
  review_status: string;
  /** The counterparty's ID number: the supplier on expenses, the customer on sales. */
  counterparty_number: string | null;
  /** Matched by TEXT rules when the transaction has no lines. */
  description?: string | null;
  business_use_source?: string | null;
  /** RTS category of a transaction without lines. */
  rts_category_source?: string | null;
}

export interface TransactionClassification {
  /** Per input line: what to write (null = keep: the user's decision, or unknown facts). */
  lines: { classification: LineClassification | IncomeLineClassification | null; rts: RtsAssignment | null }[];
  businessUse: BusinessUseAssignment | null;
  /** RTS category of an expense without lines (null = keep). */
  rts: RtsAssignment | null;
  review_status: string;
}

/** Classify a transaction and its lines with the rules (see the precedence at the top). */
export function classifyTransaction(t: ClassifiableTransaction, lines: readonly ClassifiableLine[], index: RuleIndex, mode: "fresh" | "upgrade"): TransactionClassification {
  const direction: FlowDirection | null = t.direction === "INCOME" || t.direction === "EXPENSE" ? t.direction : null;
  const out: TransactionClassification = { lines: [], businessUse: null, rts: null, review_status: t.review_status };
  if (t.review_status === "CONFIRMED" || !direction) {
    out.lines = lines.map(() => ({ classification: null, rts: null }));
    return out;
  }
  const winners: (RuleMatch | null)[] = [];
  const after: { classification_source: string | null }[] = [];
  for (const l of lines) {
    const w = winningRule(direction, { counterparty: t.counterparty_number, cabys: l.cabys, description: l.description ?? null }, index);
    winners.push(w);
    let classification: LineClassification | IncomeLineClassification | null = null;
    if (l.classification_source !== "USER") {
      if (direction === "EXPENSE") classification = w ? applySets(l, w.sets, w.source, w.rule.id) : defaultClassification(l);
      else {
        const facts = saleFacts(l);
        if (facts) classification = w ? applyIncomeSets(facts, w.sets, w.source, w.rule.id) : applyIncomeSets(facts, {}, "DEFAULT", null);
      }
    }
    out.lines.push({ classification, rts: direction === "EXPENSE" ? rtsFromRule(l, w) : null });
    after.push({ classification_source: classification?.classification_source ?? l.classification_source });
  }
  if (lines.length === 0) {
    const w = winningRule(direction, { counterparty: t.counterparty_number, cabys: null, description: t.description ?? null }, index, "transaction");
    winners.push(w);
    if (direction === "EXPENSE") out.rts = rtsFromRule(t, w);
  }
  out.businessUse = businessUseFromRules(t, winners);
  out.review_status = reviewStatusFor(t.review_status, direction, after, mode, out.businessUse?.business_use_source ?? t.business_use_source ?? null);
  return out;
}

// ------------------------------------------------------------------ retroactive plan

export interface ReclassifyTransaction extends ClassifiableTransaction {
  id: string;
  business_use?: string | null;
  business_pct?: string | null;
  rts_category?: string | null;
}

export interface ReclassifyLine extends ClassifiableLine {
  id: string;
  transaction_id: string;
  classification_rule_id: string | null;
  accounting_category: string | null;
  income_tax_deductible: boolean | null;
  iva_creditable: boolean | null;
  iva_credit_type: string | null;
  rts_category?: string | null;
}

export type LinePatch = Partial<LineClassification> & Partial<IncomeLineClassification> & Partial<RtsAssignment>;
export type TransactionPatch = { review_status?: string } & Partial<BusinessUseAssignment> & Partial<RtsAssignment>;

export interface ReclassifyPlan {
  lineUpdates: { id: string; patch: LinePatch }[];
  transactionUpdates: { id: string; patch: TransactionPatch }[];
}

const sameDecimal = (a: string | null | undefined, b: string | null | undefined) => {
  const x = a ?? null;
  const y = b ?? null;
  return x === null || y === null ? x === y : dec(x).eq(dec(y));
};

const sameExpense = (a: ReclassifyLine, b: LineClassification) =>
  a.classification_source === b.classification_source &&
  (a.classification_rule_id ?? null) === b.classification_rule_id &&
  (a.accounting_category ?? null) === b.accounting_category &&
  (a.income_tax_deductible ?? null) === b.income_tax_deductible &&
  (a.iva_creditable ?? null) === b.iva_creditable &&
  (a.iva_credit_type ?? null) === b.iva_credit_type;

const sameIncome = (a: ReclassifyLine, b: IncomeLineClassification) =>
  a.classification_source === b.classification_source &&
  (a.classification_rule_id ?? null) === b.classification_rule_id &&
  (a.tax_category ?? null) === b.tax_category &&
  sameDecimal(a.iva_rate, b.iva_rate);

const sameRts = (a: { rts_category?: string | null; rts_category_source?: string | null }, b: RtsAssignment) =>
  (a.rts_category ?? null) === b.rts_category && (a.rts_category_source ?? null) === b.rts_category_source;

const sameUse = (a: ReclassifyTransaction, b: BusinessUseAssignment) =>
  (a.business_use ?? "BUSINESS") === b.business_use && sameDecimal(a.business_pct, b.business_pct) && (a.business_use_source ?? "DEFAULT") === b.business_use_source;

/**
 * Re-apply the current rules to every unconfirmed transaction: user decisions
 * stay, everything else is recomputed (so a deleted rule falls back to the
 * next tier or the default). Review status only moves NEEDS_REVIEW ->
 * CLASSIFIED. Only real changes are returned; a changed line gets its whole
 * classification (and/or its whole RTS pair), a transaction only what changed.
 */
export function planReclassification(rules: readonly ClassificationRule[], transactions: readonly ReclassifyTransaction[], lines: readonly ReclassifyLine[]): ReclassifyPlan {
  const index = new RuleIndex(rules);
  const byTx = new Map<string, ReclassifyLine[]>();
  for (const l of lines) byTx.set(l.transaction_id, [...(byTx.get(l.transaction_id) ?? []), l]);
  const out: ReclassifyPlan = { lineUpdates: [], transactionUpdates: [] };
  for (const t of transactions) {
    if (t.review_status === "CONFIRMED" || (t.direction !== "EXPENSE" && t.direction !== "INCOME")) continue;
    const txLines = byTx.get(t.id) ?? [];
    const c = classifyTransaction(t, txLines, index, "upgrade");
    txLines.forEach((l, i) => {
      const { classification, rts } = c.lines[i]!;
      let patch: LinePatch = {};
      if (classification) {
        const same = t.direction === "EXPENSE" ? sameExpense(l, classification as LineClassification) : sameIncome(l, classification as IncomeLineClassification);
        if (!same) patch = { ...classification };
      }
      if (rts && !sameRts(l, rts)) patch = { ...patch, ...rts };
      if (Object.keys(patch).length > 0) out.lineUpdates.push({ id: l.id, patch });
    });
    const patch: TransactionPatch = {};
    if (c.review_status !== t.review_status) patch.review_status = c.review_status;
    if (c.businessUse && !sameUse(t, c.businessUse)) Object.assign(patch, c.businessUse);
    if (c.rts && !sameRts(t, c.rts)) Object.assign(patch, c.rts);
    if (Object.keys(patch).length > 0) out.transactionUpdates.push({ id: t.id, patch });
  }
  return out;
}

// ------------------------------------------------------------------ RTS categories (DES-96)

/** How an expense line's RTS category was obtained. */
export type RtsBasis = "USER" | "RULE" | "ACCOUNTING" | "CABYS" | "NONE";

export interface EffectiveRtsCategory {
  /** Null = nothing decides it (unclassified service, or no data at all). */
  category: RtsCategory | null;
  basis: RtsBasis;
  /** The CABYS code says it is a service (sections 5-9). */
  service: boolean;
}

/**
 * Accounting categories whose RTS component is clear from Decreto 43881-H
 * art. 4: merchandise, fuel, and the production costs the article names
 * ("electricidad, teléfono, agua, alquileres"), professional services, and
 * personal spending. The others fall through to the CABYS code.
 */
const RTS_BY_ACCOUNTING: Readonly<Record<string, RtsCategory>> = {
  COST_OF_SALES: "PURCHASE",
  FUEL: "FUEL",
  UTILITIES: "PRODUCTION_COST",
  RENT: "PRODUCTION_COST",
  PROFESSIONAL_SERVICES: "SERVICES_SALARIES",
  PERSONAL: "EXCLUDED",
};

/**
 * RTS component from a CABYS code. CABYS follows the CPC 2.1 structure in its
 * leading digits [verificar against the BCCR catalog]: sections 0-4 are goods,
 * 5-9 services. Named exceptions (DES-96: a retailer's utility bills are not
 * purchases of goods): 17 electricity, gas, steam; 18 water; 691/692
 * electricity, gas and water distribution; 841/842 telephony and internet;
 * 721 rental of real estate -> production costs; 333 petroleum fuels -> fuel.
 * A service without one of those has no category: its RTS treatment depends
 * on the activity and on what the service is.
 */
export function rtsCategoryFromCabys(code: string | null | undefined): { category: RtsCategory | null; service: boolean } | null {
  const c = normalizeCabys(code);
  if (!c) return null;
  if (/^(17|18)/.test(c)) return { category: "PRODUCTION_COST", service: false };
  if (/^(69[12]|84[12]|721)/.test(c)) return { category: "PRODUCTION_COST", service: true };
  if (/^333/.test(c)) return { category: "FUEL", service: false };
  if (/^[0-4]/.test(c)) return { category: "PURCHASE", service: false };
  return { category: null, service: true };
}

/** The RTS component of an expense line: decided (user, rule) or derived (accounting category, CABYS). */
export function effectiveRtsCategory(line: { rts_category?: string | null; rts_category_source?: string | null; accounting_category?: string | null; cabys?: string | null }): EffectiveRtsCategory {
  if (line.rts_category && RTS_CODES.has(line.rts_category)) {
    return { category: line.rts_category as RtsCategory, basis: line.rts_category_source === "USER" ? "USER" : "RULE", service: false };
  }
  const byAccounting = line.accounting_category ? RTS_BY_ACCOUNTING[line.accounting_category] : undefined;
  if (byAccounting) return { category: byAccounting, basis: "ACCOUNTING", service: false };
  const byCabys = rtsCategoryFromCabys(line.cabys);
  if (byCabys) return { ...byCabys, basis: "CABYS" };
  return { category: null, basis: "NONE", service: false };
}

// ------------------------------------------------------------------ AI suggestions (plan §14.3 step 6)

/**
 * An AI suggestion for an expense line of the review queue (DES-89 classify
 * feature). It is ONLY a suggestion: the review screen may prefill its
 * selects from it (aiSuggestionPrefill) and a person still saves the
 * decision, which is then recorded as the person's own (classification_source
 * USER), exactly like a decision made by hand. It never sets the IVA credit
 * of a line, never a business-use percentage and never computes amounts.
 *
 * Stored in ai_classification_suggestions.suggestion (the confidence has its
 * own column); private.ai_classification_suggestion_valid checks the same
 * shape and contradictions in the database (supabase/tests/ai_classify.test.ts
 * runs both on the same cases).
 */
export interface AiClassificationSuggestion {
  /** ACCOUNTING_CATEGORIES code; null = the AI is not sure. */
  accounting_category: string | null;
  /** Income-tax deductibility; null = the AI is not sure. */
  income_tax_deductible: boolean | null;
  /** Business use of the invoice; null = not sure. Never a MIXED percentage: that share is the person's call. */
  business_use: BusinessUse | null;
  /** RTS base component, RTS taxpayers only; null otherwise or when not sure. */
  rts_category: RtsCategory | null;
  /** One line of Spanish text (why), shown next to the suggestion. */
  reason: string;
}

export const AI_SUGGESTION_KEYS = ["accounting_category", "income_tax_deductible", "business_use", "rts_category", "reason"] as const satisfies readonly (keyof AiClassificationSuggestion)[];
/** Longest reason kept, in characters (code points). */
export const AI_REASON_MAX = 200;
/** From this confidence on the suggestion reads "Sugerido por IA"; below it, "IA no está segura". */
export const AI_CONFIDENCE_SURE = "0.6";

export type AiSuggestionValidation = { ok: true; suggestion: AiClassificationSuggestion } | { ok: false; reason: string };

// Control, format, private-use, unassigned, surrogate code points and line/paragraph separators: never in a reason.
const REASON_BAD = /[\p{Cc}\p{Cf}\p{Co}\p{Cn}\p{Cs}\p{Zl}\p{Zp}]/u;
const REASON_BAD_ALL = /[\p{Cc}\p{Cf}\p{Co}\p{Cn}\p{Cs}\p{Zl}\p{Zp}]/gu;

/**
 * What the review screen shows instead of a reason that breaks the rules
 * below (the classification itself is kept: it can only hold allowed values).
 */
export const AI_REASON_WITHHELD = "Motivo no disponible.";

// Latin letters, Spanish accents included (U+00C0-U+017E without the multiplication and division signs; U+017F,
// the long s, is left out because JavaScript's case-insensitive matching folds it to "s" and Postgres does not).
const REASON_LETTERS = "A-Za-zÀ-ÖØ-öø-ž";

/**
 * The characters a reason may use, as a JSON Schema `pattern` (sent to the
 * model, which cannot write anything else) and checked again here: letters,
 * the space and plain punctuation. No digit of any script, no currency or
 * percent sign, no "@", "#", "<", ">", "[", "]", "&", "+", "=", "*", "_" or
 * emoji. Literal characters only (no escapes), so every regex engine reads it
 * the same. private.ai_classification_suggestion_valid builds the same set.
 */
export const AI_REASON_PATTERN = `^[ ${REASON_LETTERS}.,;:?!()"'/¿¡«»–—‘’“”…-]+$`;
const REASON_ALLOWED = new RegExp(AI_REASON_PATTERN, "u");
// Punctuation a reason may use (AI_REASON_PATTERN) to wrap a word or a dot: quotes, guillemets, brackets, slash,
// colon and dashes. Hyphen last, so it is literal inside a character class.
const REASON_WRAP_OPEN = `("'«“‘/:–—-`;
const REASON_WRAP_CLOSE = `)"'»”’/:–—-`;
const REASON_WRAP_ANY = `"'«»“”‘’()/:–—-`;
// How a domain ends when it is spelled out in words ("hacienda punto gob", "hacienda dot com"). Only for that spelled-out
// form: a written dot needs no list (below).
const SPELLED_ENDINGS = [
  "com", "net", "org", "gob", "gov", "go", "cr", "co", "io", "ly", "info", "biz", "app", "online", "site", "edu", "mx", "xyz", "link", "click", "tv", "cc",
  "top", "shop", "store", "bank", "pay", "money", "dev", "cloud", "ai", "page", "email", "live", "world", "lat",
];
// Words that commonly follow "punto" in prose ("punto por punto", "punto de venta"): not a domain's middle label.
const PROSE_AFTER_PUNTO = ["por", "de", "del", "la", "las", "el", "los", "en", "con", "para", "tras", "sin", "que", "y", "a", "final", "clave", "muerto"];

/**
 * A link, or anything shaped like one. No list of endings for a written dot:
 * new top-level domains appear all the time (".gob", ".bank", ".pagos"), so
 * ANY domain-like token counts: a run of dots (or an ellipsis) with a non-space
 * character before it and two letters after it, optionally behind quotes,
 * guillemets, brackets, a slash, a colon or dashes ("hacienda-pagos.gob",
 * "x.go.cr", "bit.ly/abc", "(hacienda).gob", "«hacienda».gob", "hacienda.’gob",
 * "hacienda/.gob", "hacienda..gob", "hacienda…gob"). Also:
 * - "://", "www" as a word, spaced, dotted or glued ("www hacienda gob", "w w
 *   w", "W.W.W", "wwwhacienda"), a scheme ("https:", the defanged "hxxps:",
 *   "https" as a word), punycode ("xn--");
 * - a defanged dot: ".", "punto" or "dot" inside any pair of those wrappers
 *   ("(.)", '"."', "«.»", "-.-", "/./", ":.:", "«punto»", "(punto)");
 * - a dot with a space before it ("hacienda .gob", "h a c i e n d a . g o b");
 * - a dot, a space and a usual ending in lower case ("hacienda. gob");
 * - a domain spelled out, between spaces or wrappers: "punto"/"dot" joining
 *   words twice ("hacienda punto gob punto cr") or before a usual ending
 *   ("hacienda punto gob", "«hacienda» punto «gob»", "hacienda dot com"), or
 *   glued in capitals ("haciendaPUNTOgob").
 * A reason is prose: "S.A." (one letter after each dot), "p. ej." (a space),
 * "punto de venta", "punto a punto" and "punto por punto" pass; a glued
 * sentence ("negocio.La") is withheld, which only costs the reason.
 */
// Between the words of a domain spelled out: spaces or wrappers ("hacienda punto gob", "«hacienda» punto «gob»",
// "hacienda-dot-gob"). Hyphen last in the class.
const REASON_SPELLED_GAP = String.raw`[\s${REASON_WRAP_ANY}]+`;
const REASON_LINK = new RegExp(
  [
    "://",
    // "www" as a word, spaced, dotted or glued: "www hacienda", "w w w", "W.W.W", "wwwhacienda" (no Spanish word starts so).
    String.raw`(?<![${REASON_LETTERS}])w[\s.-]?w[\s.-]?w`,
    // "www." anywhere, as the database's older check has it: this check must never be looser than that one.
    "www[.]",
    // A scheme: "https:", the defanged "hxxps:", or "http"/"https" as a word ("https hacienda gob"); punycode ("xn--").
    String.raw`(?<![${REASON_LETTERS}])(?:https?|hxxps?|ftp)\s?:`,
    String.raw`(?<![${REASON_LETTERS}])(?:https?|hxxps?)(?![${REASON_LETTERS}])`,
    String.raw`(?<![${REASON_LETTERS}])xn--`,
    String.raw`[${REASON_WRAP_OPEN}]\s?(?:[.…]+|punto|dot)\s?[${REASON_WRAP_CLOSE}]`,
    // A dot with a space before it and a letter after it ("hacienda .gob", "h a c i e n d a . g o b").
    String.raw`\s[.]\s?[${REASON_LETTERS}]`,
    String.raw`[^\s][.…]+[${REASON_WRAP_ANY}]*[${REASON_LETTERS}]{2}`,
    String.raw`[${REASON_LETTERS}]${REASON_SPELLED_GAP}(?:punto|dot)${REASON_SPELLED_GAP}(?:${SPELLED_ENDINGS.join("|")})(?![${REASON_LETTERS}])`,
    String.raw`[${REASON_LETTERS}]{2}${REASON_SPELLED_GAP}(?:punto|dot)${REASON_SPELLED_GAP}(?!(?:${PROSE_AFTER_PUNTO.join("|")})(?![${REASON_LETTERS}]))[${REASON_LETTERS}]{2,}${REASON_SPELLED_GAP}(?:punto|dot)${REASON_SPELLED_GAP}[${REASON_LETTERS}]{2}`,
  ].join("|"),
  "iu",
);
// Case-sensitive, where capitals tell a link from prose:
// - "punto"/"dot" glued between words ("haciendaPUNTOgob", "haciendaDotgob"), not "anécdota" or "contrapunto";
// - a dot, a space and a usual ending in lower case as a whole word ("hacienda. gob", "hacienda. gob. cr"): a sentence
//   starts with a capital ("Uso del negocio. App de facturación"), and "gobierno" or "go-kart" are not endings.
const REASON_LINK_CASED = new RegExp(
  [
    String.raw`\p{Ll}(?:PUNTO|Punto|DOT|Dot)[${REASON_LETTERS}]`,
    String.raw`[${REASON_LETTERS}]{2}[.]\s+(?:${SPELLED_ENDINGS.join("|")})(?![${REASON_LETTERS}-])`,
  ].join("|"),
  "u",
);
// Spanish plurals abbreviated with doubled capitals ("EE.UU.", "RR.HH.", "FF.AA."): not domains. Case-sensitive, and
// only as a whole token ("EE.UU.com" stays a link). "p.ej." (por ejemplo) written without its space is prose too.
const REASON_PLURAL_ABBREVIATION = new RegExp(String.raw`(?<![${REASON_LETTERS}.])(?:([A-Z])\1[.])+([A-Z])\2[.]?(?![${REASON_LETTERS}.])`, "gu");
const REASON_POR_EJEMPLO = new RegExp(String.raw`(?<![${REASON_LETTERS}.])p[.]\s?ej[.](?![${REASON_LETTERS}.])`, "giu");

/** Whether a reason holds a link or a domain-like token (REASON_LINK), after setting prose abbreviations aside. */
function reasonHasLink(reason: string): boolean {
  const prose = reason.replace(REASON_PLURAL_ABBREVIATION, " ").replace(REASON_POR_EJEMPLO, " ");
  return REASON_LINK.test(prose) || REASON_LINK_CASED.test(prose);
}
// Money and percentages in words (the digits and signs are already refused): currencies, "mil", "millón", "por ciento".
const REASON_MONEY = new RegExp(
  `(?<![${REASON_LETTERS}])(?:col[oóÓ]n|colones|d[oóÓ]lar|d[oóÓ]lares|euros?|crc|usd|eur|mil|millones|mill[oóÓ]n)(?![${REASON_LETTERS}])|por ?ciento`,
  "iu",
);
// IVA credit is never the AI's call (a suggestion has no IVA-credit field): "crédito fiscal / de IVA", "acreditable", "prorrata".
const REASON_IVA_CREDIT = /cr[eéÉ]ditos? (?:fiscal|tributario|de iva|del iva|de impuesto|del impuesto)|acredit|prorrata|pro rata|iva (?:soportado|recuperable|a favor)/iu;

/**
 * Whether a model's reason (already normalized, see normalizeAiReason) may be
 * stored and shown under the "Sugerido por IA" chip: allowed characters only
 * (AI_REASON_PATTERN) and nothing that states an amount, a percentage, an IVA
 * credit or a link. The prompt asks for the same; this is the check in code,
 * mirrored by the database (private.ai_classification_suggestion_valid; its
 * link check still uses the older list of endings until a migration brings
 * REASON_LINK over, so the database is the looser of the two and never
 * refuses what this accepts). A reason that fails is replaced by
 * AI_REASON_WITHHELD before it is stored.
 */
export function aiReasonAllowed(reason: string): boolean {
  return REASON_ALLOWED.test(reason) && !reasonHasLink(reason) && !REASON_MONEY.test(reason) && !REASON_IVA_CREDIT.test(reason);
}

/**
 * A model's free-text reason as it may be stored: one line, invisible and
 * control characters removed, spaces collapsed, cut to AI_REASON_MAX
 * characters (never inside a surrogate pair). Empty when nothing is left.
 */
export function normalizeAiReason(text: unknown): string {
  if (typeof text !== "string") return "";
  const one = text.normalize("NFC").replace(REASON_BAD_ALL, " ").replace(/\s+/g, " ").trim();
  const chars = [...one];
  return chars.length <= AI_REASON_MAX ? one : `${chars.slice(0, AI_REASON_MAX - 1).join("").trimEnd()}…`;
}

/**
 * Validate a suggestion before it is stored or shown: allowed values only and
 * no contradictions. Strict: every key present and nothing else, the reason
 * already normalized (normalizeAiReason). `rts` = the taxpayer is in the RTS.
 */
export function validateAiSuggestion(raw: unknown, ctx: { rts: boolean }): AiSuggestionValidation {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ok: false, reason: "Sugerencia vacía" };
  const r = raw as Record<string, unknown>;
  const unknownKey = Object.keys(r).find((k) => !(AI_SUGGESTION_KEYS as readonly string[]).includes(k));
  if (unknownKey) return { ok: false, reason: `Campo no permitido: ${unknownKey}` };
  const missing = AI_SUGGESTION_KEYS.find((k) => !(k in r));
  if (missing) return { ok: false, reason: `Falta el campo ${missing}` };
  const { accounting_category: cat, income_tax_deductible: ded, business_use: use, rts_category: rts, reason } = r;
  if (cat !== null && (typeof cat !== "string" || !CATEGORY_CODES.has(cat))) return { ok: false, reason: "Categoría contable desconocida" };
  if (ded !== null && typeof ded !== "boolean") return { ok: false, reason: "Deducibilidad inválida" };
  if (use !== null && (typeof use !== "string" || !USE_CODES.has(use))) return { ok: false, reason: "Uso inválido (negocio, personal o mixto)" };
  if (rts !== null && (typeof rts !== "string" || !RTS_CODES.has(rts))) return { ok: false, reason: "Categoría RTS desconocida" };
  if (rts !== null && !ctx.rts) return { ok: false, reason: "La base RTS solo aplica al régimen simplificado" };
  if (typeof reason !== "string" || reason.length === 0 || [...reason].length > AI_REASON_MAX || reason !== reason.trim() || REASON_BAD.test(reason) || /\s\s|[\t\n\r]/.test(reason)) {
    return { ok: false, reason: `Motivo inválido (una línea, 1 a ${AI_REASON_MAX} caracteres)` };
  }
  if (!aiReasonAllowed(reason)) return { ok: false, reason: "Motivo no permitido (sin cifras, montos, porcentajes, crédito de IVA ni enlaces)" };
  const personal = cat === "PERSONAL" || use === "PERSONAL";
  if (personal && ded === true) return { ok: false, reason: "Un gasto personal no es deducible" };
  if (cat === "PERSONAL" && (use === "BUSINESS" || use === "MIXED")) return { ok: false, reason: "Categoría personal con uso del negocio" };
  if (personal && rts !== null && rts !== "EXCLUDED") return { ok: false, reason: "Un gasto personal no forma parte de la base RTS" };
  return {
    ok: true,
    suggestion: {
      accounting_category: cat as string | null,
      income_tax_deductible: ded as boolean | null,
      business_use: use as BusinessUse | null,
      rts_category: rts as RtsCategory | null,
      reason,
    },
  };
}

/** Nothing to prefill: the AI was unsure of every decision. */
export function aiSuggestionEmpty(s: AiClassificationSuggestion): boolean {
  return s.accounting_category === null && s.income_tax_deductible === null && s.business_use === null && s.rts_category === null;
}

/**
 * A confidence as stored (numeric(4,3)): a decimal string with three places
 * from "0.000" to "1.000", or null when `v` is not one. Accepts the model's
 * JSON number or the database's text.
 */
export function normalizeAiConfidence(v: unknown): string | null {
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 && v <= 1 ? v.toFixed(3) : null;
  if (typeof v === "string" && /^(0(\.\d{1,6})?|1(\.0{1,6})?)$/.test(v.trim())) return dec(v.trim()).toDecimalPlaces(3).toFixed(3);
  return null;
}

/** Whether the AI is sure enough to say "Sugerido por IA" (else "IA no está segura"). */
export function aiConfidenceSure(confidence: string): boolean {
  return dec(confidence).gte(AI_CONFIDENCE_SURE);
}

/** "0.824" -> "82": whole percent, half up, for "Sugerido por IA · 82 %". */
export function aiConfidencePercent(confidence: string): string {
  return dec(confidence).times(100).toDecimalPlaces(0).toFixed(0);
}

/** What "Usar sugerencia" puts in the line editor's selects. An absent key leaves that select as it is. */
export interface AiPrefill {
  accounting_category?: string;
  income_tax_deductible?: boolean;
  iva_credit_type?: IvaCreditType;
  rts_category?: RtsCategory;
  business_use?: BusinessUse;
}

/**
 * Turn a suggestion into prefill values for an expense line, under the same
 * domain rules as any decision: a line whose XML carries no IVA stays without
 * credit (applySets), a personal expense is not deductible and has no IVA
 * credit (what the editor does when a person picks "Personal"), and the RTS
 * base is offered to RTS taxpayers only. A suggestion never adds an IVA
 * credit: for a taxed business expense the editor keeps its current choice.
 * A person still saves the result; nothing here touches the line.
 */
export function aiSuggestionPrefill(line: { tax_category: string | null }, s: AiClassificationSuggestion, opts: { rts: boolean }): AiPrefill {
  const out: AiPrefill = {};
  const personal = s.accounting_category === "PERSONAL" || s.business_use === "PERSONAL";
  if (s.accounting_category !== null) out.accounting_category = s.accounting_category;
  const deductible = personal ? false : s.income_tax_deductible;
  if (deductible !== null) out.income_tax_deductible = deductible;
  if (!lineIsTaxed(line) || personal) out.iva_credit_type = applySets(line, personal ? { iva_credit_type: "NONE" } : {}, "USER", null).iva_credit_type;
  if (opts.rts && s.rts_category !== null) out.rts_category = s.rts_category;
  if (s.business_use !== null) out.business_use = s.business_use;
  return out;
}
